import {Component, DestroyRef, HostListener, OnInit, QueryList, ViewChildren} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {ActivatedRoute} from '@angular/router';
import {
  AbstractControl, FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl,
  UntypedFormGroup, ValidationErrors, Validators
} from '@angular/forms';
import {DynamicFormItemsService} from '../../../../service/dynamic-form-items.service';
import {StringFormatterService} from '../../../../service/utilities/string-formatter.service';
import {NavigationService} from '../../../../service/navigation.service';
import {Title} from '@angular/platform-browser';
import {
  AdTypesSettingsService,
  OfferSetFullRefreshResponse,
  OfferSetRefreshCondition
} from '../../../../service/ad-types-settings.service';
import {AccountAdTypesInterface} from '../../../../model/account-ad-types.interface';
import {BusinessCategoryConfigurationsService} from '../../../../service/business-category-configurations.service';
import {firstValueFrom, Observable} from 'rxjs';
import {BusinessSubcategoryInterface} from '../../../../model/business-subcategory.interface';
import {FeatureFlagGuard} from "../../../../feature-flag.guard";
import {OfferRulesComponent} from './offer-rules/offer-rules.component';
import {ConditionalIncentiveConfiguratorComponent} from './conditional-incentive-configurator/conditional-incentive-configurator.component';
import {UserService} from '../../../../service/user.service';

type FeeValueFormValue = {
  value: number | string | null;
};

type DynamicFeeRowFormValue = {
  id?: number | null;
  feeType?: string | null;
  type?: string | null;
  customFeeName?: string | null;
  value: number | string | null;
  isPercent?: boolean | null;
  purchaseBehavior?: string | null;
  purchaseDisplayName?: string | null;
  leaseBehavior?: string | null;
  financeBehavior?: string | null;
  isDefaultDocumentFee?: boolean | null;
  sortOrder?: number | null;
  enabled?: boolean | null;
  accountBusinessCategoryGuid?: string | null;
  displayName?: string | null;
  finalPriceText?: string | null;
};

type LegacyFeeFormValue = {
  id?: number | null;
  type?: string | null;
  value?: number | string | null;
  isPercent?: boolean | null;
  leaseApplication?: string | null;
  financeApplication?: string | null;
  displayName?: string | null;
  enabled?: boolean | null;
  accountBusinessCategoryGuid?: string | null;
  pricingOption?: string | null;
  finalPriceText?: string | null;
};

type FeesConfigurationFormValue = {
  documentFee: FeeValueFormValue;
  licenseFee: FeeValueFormValue;
  titleFee: FeeValueFormValue;
  acquisitionFee: FeeValueFormValue;
  taxes: FeeValueFormValue;
  dispositionFee: FeeValueFormValue;
  customFee: FeeValueFormValue;
  rows?: DynamicFeeRowFormValue[];
  finalPriceText?: string | null;
};

type PurchaseConfigurationFormValue = {
  dealerAddOnLabel?: string | null;
  [key: string]: unknown;
};

type BusinessCategoryConfigurationFormValue = {
  feesConfiguration: FeesConfigurationFormValue;
  purchaseConfiguration?: PurchaseConfigurationFormValue;
  [key: string]: unknown;
};

type LeaseTermSelection = 'fixedTerm' | 'lowestEstimatedPayment';

type LeaseTermSelectionOption = {
  label: string;
  value: LeaseTermSelection;
};

type PriceSubtotalBoldOption = {
  label: string;
  value: boolean | null;
};

@Component({
    selector: 'auto-new-ad-types',
    templateUrl: './new-ad-types.component.html',
    styleUrls: ['./new-ad-types.component.scss'],
    standalone: false
})
export class NewAdTypesComponent implements OnInit {
  @ViewChildren(OfferRulesComponent) offerRulesComponents: QueryList<OfferRulesComponent>;
  @ViewChildren(ConditionalIncentiveConfiguratorComponent) conditionalIncentiveComponents: QueryList<ConditionalIncentiveConfiguratorComponent>;
  private readonly leasePricingBaseDefault = 'discountPrice';
  readonly defaultDealerAddOnLabel = 'Dealer Add-On & Adjustment';
  readonly dealerAddOnLabelMaxLength = 100;
  dealerAddOnLabelEnabled = false;
  dealerAddOnLabelControl: FormControl<string> | null = null;
  private readonly dealerAddOnLabelFeatureFlag = 'dealerAddOnLabel';
  private readonly dealerAddOnLabelCategory = 'New';
  private readonly dealerAddOnLabelEdgeWhitespace = /^[\p{Z}\s\u0085\uFEFF]+|[\p{Z}\s\u0085\uFEFF]+$/gu;
  readonly defaultPriceSubtotalLabel = 'Sale Price';
  private readonly defaultPriceSubtotalLabelFontSize = 10;
  private readonly defaultPriceSubtotalValueFontSize = 12;
  readonly minimumPriceSubtotalFontSize = 1;
  readonly priceSubtotalLabelMaxLength = 100;
  readonly priceSubtotalBoldOptions: PriceSubtotalBoldOption[] = [
    {label: 'Default', value: null},
    {label: 'Bold', value: true}
  ];
  accountId: number;
  accountAdTypes: AccountAdTypesInterface | null = null;
  businessCategoryConfigurations = null;
  newBusinessCategoryType = null;
  usedBusinessCategoryType = null;
  formItems;
  componentName = 'Vehicle Ads';
  masterFormGroup: UntypedFormGroup | null = null;
  newMoneyFactorMarkupControl: UntypedFormControl;
  usedMoneyFactorMarkupControl: UntypedFormControl;
  leasePaymentTypeControl: UntypedFormControl;
  calculateLeaseFromControl: UntypedFormControl;
  leaseTermSelectionControl: UntypedFormControl | null = null;
  settingsSideNav: MenuItem[];
  accountServiceCategories: BusinessSubcategoryInterface[];
  callsToAction: UntypedFormArray;
  actionOptions: Observable<any> | null;
  decimalPattern = '^(0(\\.\\d+)?|\\.\\d*[1-9]\\d*)$';
  targetOptions = [
    {label: 'Current Window', value: '_self'},
    {label: 'New Window/Tab', value: '_blank'}
  ];
  expandIcon = 'pi pi-chevron-right';
  collapseIcon = 'pi pi-chevron-down';
  feeType = [
    {label: 'Include in Payment', value: 'includeInPayment'},
    {label: 'Due at Signing', value: 'dueAtSigning'},
    {label: 'Extra', value: 'extra'},
    {label: 'Not Used', value: 'notUsed'}
  ];
  rebateDisclosureFormatOptions = [
    {value: 'grouped', label: 'Grouped'},
    {value: 'itemized', label: 'Itemized'}
  ];
  leaseSellingPriceOptions = [
    {value: 'discountPrice', label: 'Discount price'},
    {value: 'msrp', label: 'MSRP'}
  ];
  leaseIncentiveTypeOptions = [
    {value: 'subvented fallback', label: 'Subvented (Fallback to Standard)'},
    {value: 'subvented no fallback', label: 'Subvented (No Fallback)'},
    {value: 'standard', label: 'Standard'}
  ];
  financeAprSourceOptions = [
    {value: 'incentiveService', label: 'Incentive Service'},
    {value: 'incentiveServiceFallback', label: 'Incentive Service (Fallback to Account Defaults)'},
    {value: 'accountDefaults', label: 'Account Defaults'}
  ];
  usedFinanceAprSourceOptions = [
    {value: 'accountDefaults', label: 'Account Defaults'}
  ];
  downPaymentBasedOnOptions = [
    {value: 'buy price', label: 'Buy Price'},
    {value: 'msrp', label: 'MSRP'}
  ];
  aprSourceOptions = [
    {value: true, label: 'Incentive Service (Fallback to Account Defaults)'},
    {value: false, label: 'Account Defaults'}
  ];
  calculateLeaseFromOptions = [
    {value: 'leaseDownPayment', label: 'Down Payment'},
    {value: 'dueAtSigning', label: 'Due at Signing'}
  ];
  leasePaymentTypeOptions = [
    {value: 'dollar', label: '$'},
    {value: 'percent', label: '%'}
  ];
  leasePricingBaseOptions = [
    {value: 'discountPrice', label: 'Discount Price'},
    {value: 'msrp', label: 'MSRP'},
    {value: 'capCost', label: 'Cap Cost'}
  ];
  pricingOptions = [
    {value: 'addToSalePrice', label: 'Add Purchase Fee to Sale Price'},
    {value: 'includeInSalePrice', label: 'Purchase Fee is Included in Sale Price'}
    ];
  dirtyOfferRulesForms = 0;
  dirtyConditionalIncentiveForms = 0;
  conditionalIncentiveFormValid = true;
  conditionalIncentiveFeatureFlagEnabled = false;
  displayConditionalIncentivesAsAvailable = false;
  hideLeaseOfferWithDealerAddonEnabled = false;
  hideFinanceOfferWithDealerAddonEnabled = false;
  includeFeesInSellingPriceEnabled = false;
  dynamicFeesTableEnabled = false;
  carsActTotalPriceEnabled = false;
  lowestEstimatedPaymentTermEnabled = false;
  offerSetRecalculationOnSettingSaveEnabled = false;
  isSavingVehicleAds = false;
  showVehicleAdsSaveDialog = false;
  readonly offerSetRecalculationOnSettingSaveFeatureFlag = 'enableOfferSetRecalculationOnSettingSave';
  private readonly carsActTotalPriceFeatureFlag = 'carsActTotalPrice';
  leaseDownTextOptions = [
    {value: '1', label: 'down'},
    {value: '2', label: 'due at signing'},
    {value: '3', label: 'due at signing after rebate'},
    {value: '4', label: 'cash or trade down'},
    {value: '5', label: 'down payment'}
  ];
  financePaymentDownTypeOptions = [
    {value: 'percent', label: 'Percentage Down'},
    {value: 'dollar', label: 'Dollar Down Amount'}
  ];
  financePaymentDownPercentageOptions = Array.from({length: 31}, (_, percent) => ({
    value: percent / 100,
    label: `${percent}%`
  }));
  readonly fixedTermLeaseTermSelection: LeaseTermSelection = 'fixedTerm';
  readonly lowestEstimatedPaymentLeaseTermSelection: LeaseTermSelection = 'lowestEstimatedPayment';
  readonly leaseTermSelectionExplanation = 'The Lease Term setting does not apply when Lowest Estimated Payment is selected. The optimal lease term will be selected automatically.';
  readonly leaseTermSelectionOptions: LeaseTermSelectionOption[] = [
    {label: 'Fixed Term', value: this.fixedTermLeaseTermSelection},
    {label: 'Lowest Estimated Payment', value: this.lowestEstimatedPaymentLeaseTermSelection}
  ];
  applicationValues = [
    {
      label: 'Include In Pricing',
      value: 'includeInPricing'
    }
  ];
  private readonly dynamicDefaultFinalPriceText = 'Final Price';
  private readonly dynamicDefaultFeeType = 'documentFee';
  private readonly dynamicCustomFeeType = 'customFee';
  private readonly dynamicDocumentFeeType = 'documentFee';
  private readonly dynamicLicenseFeeType = 'licenseFee';
  private readonly dynamicTitleFeeType = 'titleFee';
  private readonly dynamicAcquisitionFeeType = 'acquisitionFee';
  private readonly dynamicTaxesType = 'taxes';
  private readonly dynamicDispositionFeeType = 'dispositionFee';
  private readonly dynamicFeeTypeLabels: Record<string, string> = {
    [this.dynamicDocumentFeeType]: 'Document Fee',
    [this.dynamicLicenseFeeType]: 'License Fee',
    [this.dynamicTitleFeeType]: 'Title Fee',
    [this.dynamicAcquisitionFeeType]: 'Acquisition Fee',
    [this.dynamicTaxesType]: 'Taxes',
    [this.dynamicDispositionFeeType]: 'Disposition Fee'
  };
  private readonly dynamicPurchaseBehaviorNotUsed = 'notUsed';
  private readonly dynamicApplicationBehaviorNotUsed = 'notUsed';
  private readonly dynamicApplicationBehaviorIncludeInPayment = 'includeInPayment';
  private readonly documentFeeWarningModalKey = 'documentFeeWarning';
  private readonly documentFeeWarningHeader = 'Document Fee Not Configured';
  private readonly documentFeeWarningMessage = 'A Document Fee has not been configured for this account.\n\nDealer fees that are included in the vehicle selling price may require disclosure in advertising and payment calculations based on federal or state guidelines.\n\nPlease confirm you understand that continuing without a configured Document Fee may result in incomplete fee disclosures.';
  private readonly lowestEstimatedPaymentTermFeatureFlag = 'lowestEstimatedPaymentTerm';
  private readonly leaseTermSelectionMessageSeverity = 'info';
  private readonly leaseTermSelectionMessageSummary = 'Lease Term Selection';
  private readonly leaseTermSelectionMessageLife = 5000;
  private readonly standardNotificationLife = 5000;
  private readonly refreshNotificationLife = 10000;
  private readonly newRefreshCondition: OfferSetRefreshCondition = 'new';
  private readonly usedRefreshCondition: OfferSetRefreshCondition = 'used';
  private readonly fallbackDispatchState = 'queuedForSchedulerFallback';
  private readonly activeDispatchState = 'alreadyActive';

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    private dynamicFormItemsService: DynamicFormItemsService,
    private formBuilder: UntypedFormBuilder,
    private navigationService: NavigationService,
    private stringFormatter: StringFormatterService,
    private adTypesSettingsService: AdTypesSettingsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private businessCategoryConfigurationsService: BusinessCategoryConfigurationsService,
    private featureFlagGuard: FeatureFlagGuard,
    private userService: UserService,
    private destroyRef: DestroyRef
  ) {
  }

  /**
   * Init component
   */
  ngOnInit(): void
  {
    const firstElement = 0;
    this.titleService.setTitle('Ad Types Settings');
    this.route.paramMap.subscribe((params) => {
      this.accountId = parseInt(params.get('accountId'));
      this.featureFlagGuard.checkFeatureAccess('adTypes', 'settings/ad-types/' + this.accountId);
      this.initNavigation();
      this.getAdTypesAccountSettings()
        .then(() => {
          if (this.accountAdTypes.vehicleAdType.enabled && this.accountId) {
            this.getBusinessCategoryConfigurations(this.accountId);
          }
          this.userService.getMpopFeature('conditionalIncentives').subscribe(
            (feature) => {
              this.conditionalIncentiveFeatureFlagEnabled = feature[firstElement].enabled;
            }
          );
          this.userService.getMpopFeature('displayConditionalIncentivesAsAvailable').subscribe(
            (feature) => {
              this.displayConditionalIncentivesAsAvailable = feature[firstElement].enabled;
              if (this.displayConditionalIncentivesAsAvailable) {
                this.applicationValues = [
                  {
                    label: 'Include In Pricing',
                    value: 'includeInPricing'
                  },
                  {
                    label: 'Display as Conditional',
                    value: 'displayAsAvailable'
                  }
                ];
              }
            }
          );
          this.loadDealerAddonFeatureFlags(firstElement);
          this.loadIncludeFeesInSellingPriceFeatureFlag(firstElement);
          this.loadDynamicFeesTableFeatureFlag(firstElement);
          this.loadCarsActTotalPriceFeatureFlag();
          this.loadDealerAddOnLabelFeatureFlag();
          this.loadLowestEstimatedPaymentTermFeatureFlag();
          this.loadOfferSetRecalculationOnSettingSaveFeatureFlag(firstElement);
        });
      this.actionOptions = this.adTypesSettingsService.getActionOptions();
    });
  }

  loadDealerAddonFeatureFlags(firstElement: number): void {
    this.userService.getMpopFeature('hideLeaseOfferWithDealerAddon').subscribe(
      (feature) => {
        this.hideLeaseOfferWithDealerAddonEnabled = feature[firstElement].enabled;
      },
      () => {
        this.hideLeaseOfferWithDealerAddonEnabled = false;
      }
    );

    this.userService.getMpopFeature('hideFinanceOfferWithDealerAddon').subscribe(
      (feature) => {
        this.hideFinanceOfferWithDealerAddonEnabled = feature[firstElement].enabled;
      },
      () => {
        this.hideFinanceOfferWithDealerAddonEnabled = false;
      }
    );
  }

  /**
   * Sets includes fees in selling price feature flag status
   * @param firstElement
   */
  loadIncludeFeesInSellingPriceFeatureFlag(firstElement: number): void {
    this.userService.getMpopFeature('includeFeesInSellingPrice').subscribe(
      (feature) => {
        this.includeFeesInSellingPriceEnabled = feature[firstElement].enabled;
        this.setFeeTypeLabel();
      },
      () => {
        this.includeFeesInSellingPriceEnabled = false;
        this.setFeeTypeLabel();
      }
    );
  }

  loadDynamicFeesTableFeatureFlag(firstElement: number): void
  {
    this.userService.getMpopFeature('dynamicFeesTable').subscribe(
      (feature) => {
        this.dynamicFeesTableEnabled = feature[firstElement].enabled;
      },
      () => {
        this.dynamicFeesTableEnabled = false;
      }
    );
  }

  /**
   * Fails closed when feature state is unavailable and releases the subscription on destruction.
   */
  private loadDealerAddOnLabelFeatureFlag(): void
  {
    this.userService.getMpopFeature(this.dealerAddOnLabelFeatureFlag).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (features) => {
        const feature = features.find((item) => item.name === this.dealerAddOnLabelFeatureFlag);
        this.dealerAddOnLabelEnabled = feature?.enabled === true;
        this.syncDealerAddOnLabelControlState();
      },
      error: () => {
        this.dealerAddOnLabelEnabled = false;
        this.syncDealerAddOnLabelControlState();
        console.error('Unable to load the Dealer Add-On Label feature flag; the control remains unavailable.');
      }
    });
  }

  /**
   * Supports either flag/form load order without changing edits or emitting form events.
   */
  private syncDealerAddOnLabelControlState(): void
  {
    this.dealerAddOnLabelControl?.disable({emitEvent: false});
    if (this.dealerAddOnLabelEnabled) {
      this.dealerAddOnLabelControl?.enable({emitEvent: false});
    }
  }

  /**
   * Supplies the account default for older API responses while keeping blank input valid.
   */
  private buildDealerAddOnLabelControl(value: unknown): FormControl<string>
  {
    let label = this.defaultDealerAddOnLabel;
    if (typeof value === 'string') {
      label = value;
    }
    this.dealerAddOnLabelControl = new FormControl(label, {
      nonNullable: true,
      validators: this.validateDealerAddOnLabel.bind(this)
    });
    this.syncDealerAddOnLabelControlState();

    return this.dealerAddOnLabelControl;
  }

  /**
   * Matches API Unicode edge trimming and restores the account default for blank labels.
   */
  private normalizeDealerAddOnLabel(label: string): string
  {
    let normalizedLabel = label.replace(this.dealerAddOnLabelEdgeWhitespace, '');
    if (normalizedLabel === '') {
      normalizedLabel = this.defaultDealerAddOnLabel;
    }

    return normalizedLabel;
  }

  /**
   * Counts normalized Unicode code points so emoji and blank input follow the API contract.
   */
  private validateDealerAddOnLabel(control: AbstractControl): ValidationErrors | null
  {
    const value: unknown = control.value;
    let errors: ValidationErrors | null = null;
    if (typeof value !== 'string') {
      errors = {dealerAddOnLabelType: true};
    }
    if (typeof value === 'string') {
      const normalizedLabel = this.normalizeDealerAddOnLabel(value);
      if (Array.from(normalizedLabel).length > this.dealerAddOnLabelMaxLength) {
        errors = {dealerAddOnLabelLength: true};
      }
    }

    return errors;
  }

  /**
   * Omits disabled and non-New labels even when getRawValue includes disabled controls.
   */
  private normalizeDealerAddOnLabelPayload(
    configuration: BusinessCategoryConfigurationFormValue,
    categoryName: string
  ): void
  {
    const purchaseConfiguration = configuration.purchaseConfiguration;
    if (purchaseConfiguration !== undefined) {
      const label = purchaseConfiguration.dealerAddOnLabel;
      delete purchaseConfiguration.dealerAddOnLabel;
      if (this.dealerAddOnLabelEnabled && categoryName === this.dealerAddOnLabelCategory) {
        if (typeof label !== 'string') {
          throw new Error('Dealer Add-On Label must be text.');
        }
        purchaseConfiguration.dealerAddOnLabel = this.normalizeDealerAddOnLabel(label);
      }
    }
  }

  loadCarsActTotalPriceFeatureFlag(): void
  {
    this.userService.getMpopFeature(this.carsActTotalPriceFeatureFlag).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      (features) => {
        const feature = features.find(
          (currentFeature) => currentFeature.name === this.carsActTotalPriceFeatureFlag
        );

        this.carsActTotalPriceEnabled = feature !== undefined && feature.enabled;
      },
      () => {
        this.carsActTotalPriceEnabled = false;
      }
    );
  }

  /**
   * Loads feature flag to allow full offer set refresh and recalculation
   */
  loadOfferSetRecalculationOnSettingSaveFeatureFlag(firstElement: number): void {
    this.userService.getMpopFeature(this.offerSetRecalculationOnSettingSaveFeatureFlag).subscribe(
      (feature) => {
        this.offerSetRecalculationOnSettingSaveEnabled = feature[firstElement].enabled;
      },
      () => {
        this.offerSetRecalculationOnSettingSaveEnabled = false;
      }
    );
  }

  /**
   * Loads the lowest estimated payment feature flag
   */
  loadLowestEstimatedPaymentTermFeatureFlag(): void
  {
    this.userService.getMpopFeature(this.lowestEstimatedPaymentTermFeatureFlag).subscribe(
      (features) => {
        const feature = features.find((currentFeature) => currentFeature.name === this.lowestEstimatedPaymentTermFeatureFlag);

        this.lowestEstimatedPaymentTermEnabled = feature !== undefined && feature.enabled;
        this.initializeMissingLeaseTermSelection();
      },
      () => {
        this.lowestEstimatedPaymentTermEnabled = false;
      }
    );
  }

  /**
   * Set fee type label based on include fees in selling price feature flag
   */
  setFeeTypeLabel(): void {
    let includeInPaymentLabel = 'Include in Payment';

    if (this.includeFeesInSellingPriceEnabled) {
      includeInPaymentLabel = 'Include in Selling Price';
    }

    this.feeType[0].label = includeInPaymentLabel;
  }

  /**
   * Init navigation
   */
  initNavigation() {
    this.navigationService.getMenuItems(this.accountId, 'settings-view-side-menu.configuration.json')
      .then((navItems) => {
        this.settingsSideNav = navItems;
      });
  }

  /**
   * Get ad types account settings -- needed to set this.vehicleAdsEnabled
   */
  getAdTypesAccountSettings() {
    return new Promise(((resolve, reject) => {
      this.adTypesSettingsService.getAdTypesAccountSettings(this.accountId)
        .subscribe(
          (result: AccountAdTypesInterface) => {
            this.accountAdTypes = result;
            this.parseAdTypes(result);
            resolve(null);
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Unable to find account info. Please try again.',
              life: 5000
            });
            reject();
          });
    }));
  }

  /**
   *  This is a dirty function to be able to parse the ad types into the defined interface
   * @param adTypes
   */
  parseAdTypes(adTypes) {
    adTypes.forEach((adType) => {
      this.accountAdTypes.accountId = adType.accountId;
      if (adType.name === 'vehicle') {
        this.accountAdTypes.vehicleAdType = adType;
      }
    });
  }

  /**
   * Get account's business category configurations & init the vehicle ads form
   *
   * @param accountId
   */
  getBusinessCategoryConfigurations(accountId) {
    this.businessCategoryConfigurationsService.getBusinessCategoryConfigurations(this.componentName, accountId)
      .subscribe(
        (result) => {
          this.businessCategoryConfigurations = result;
          const empty: any = {};
          if (this.businessCategoryConfigurations['businessCategoryTypes'] !== empty && this.masterFormGroup === null) {
            this.initForm(this.businessCategoryConfigurations['businessCategoryTypes']);
          }
        },
        () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Unable to find business category configurations. Please try again.',
            life: 5000
          });
        });
  }

  /**
   * init form - trigger build FormItems, FormControls, and FormGroups
   */
  initForm(businessCategories: any[]) {
    const formItemsResult = {};
    businessCategories.forEach((businessCategory) => {
      const businessCategoryName = businessCategory.name;
      if  (businessCategoryName === 'New') {
        this.newBusinessCategoryType = businessCategory;
        formItemsResult[businessCategoryName] = this.handleBuildNewForm(businessCategory);
      } else if (businessCategoryName === 'Used') {
        this.usedBusinessCategoryType = businessCategory;
        formItemsResult[businessCategoryName] = this.handleBuildUsedForm(businessCategory);
      }
    });
    this.formItems = formItemsResult;
    businessCategories.forEach((businessCategory) => {
      const businessCategoryName = businessCategory['name'];
      this.formItems[businessCategoryName]['feesConfiguration'] = this.handleBuildFeesForm(businessCategory);
    });
    this.masterFormGroup = this.buildFormGroups(this.formItems);
    this.setIncentiveControlsStatus();
  }

  /**
   * Helper function to build New tab
   * @param businessCategory
   */
  handleBuildNewForm(businessCategory)
  {
    const configuration = businessCategory['configuration'];
    const businessCategoryFormItemsResult = {};
    let leaseTermSelection = configuration.leaseConfiguration.leaseTermSelection;

    if (leaseTermSelection === null) {
      leaseTermSelection = undefined;
    }
    if (this.lowestEstimatedPaymentTermEnabled && leaseTermSelection === undefined) {
      leaseTermSelection = this.fixedTermLeaseTermSelection;
    }

    businessCategoryFormItemsResult['aprConfiguration'] = new UntypedFormGroup({
      showAprOffer: new UntypedFormControl(configuration.aprConfiguration.showAprOffer),
      useChromeIncentivesAprOffer: new UntypedFormControl(configuration.aprConfiguration.useChromeIncentivesAprOffer),
      aprPricingRate: new UntypedFormControl(configuration.aprConfiguration.aprPricingRate),
      aprPricingTerm: new UntypedFormControl(configuration.aprConfiguration.aprPricingTerm)
    });
    businessCategoryFormItemsResult['financeConfiguration'] = new UntypedFormGroup({
      showFinanceOffer: new UntypedFormControl(configuration.financeConfiguration.showFinanceOffer),
      financeAprSource: new UntypedFormControl(configuration.financeConfiguration.financeAprSource),
      downPaymentBasedOn: new UntypedFormControl(configuration.financeConfiguration.downPaymentBasedOn),
      includeFinanceDealerIncentives: new UntypedFormControl(configuration.financeConfiguration.includeFinanceDealerIncentives),
      hideFinanceOfferWithDealerAddon: new UntypedFormControl(
        configuration.financeConfiguration.hideFinanceOfferWithDealerAddon ?? false
      ),
      showFinancePayment: new UntypedFormControl(configuration.financeConfiguration.showFinancePayment),
      financePaymentApr: new UntypedFormControl(configuration.financeConfiguration.financePaymentApr),
      showFinanceApr: new UntypedFormControl(configuration.financeConfiguration.showFinanceApr),
      financePaymentTerm: new UntypedFormControl(configuration.financeConfiguration.financePaymentTerm),
      financeDownPaymentAmount: new UntypedFormControl(this.getFinanceDownPaymentAmountFormValue(
        configuration.financeConfiguration.financeDownPaymentAmount,
        configuration.financeConfiguration.financeDownPaymentType
      )),
      financeDownPaymentType: new UntypedFormControl(configuration.financeConfiguration.financeDownPaymentType || 'percent'),
      showFinanceDownPayment: new UntypedFormControl(configuration.financeConfiguration.showFinanceDownPayment),
      daysToFirstPayment: new UntypedFormControl(configuration.financeConfiguration.daysToFirstPayment)
    });
    businessCategoryFormItemsResult['generalConfiguration'] = new UntypedFormGroup({
      includeSelectInventoryIncentives: new UntypedFormControl(configuration.generalConfiguration.includeSelectInventoryIncentives),
      rebateDisclosureFormat: new UntypedFormControl(configuration.generalConfiguration.rebateDisclosureFormat),
      availableOffersLabel: new UntypedFormControl(configuration.generalConfiguration.availableOffersLabel, [Validators.required, Validators.maxLength(100)]),
      useRateTable: new UntypedFormControl(configuration.generalConfiguration.useRateTable)
    });
    businessCategoryFormItemsResult['incentiveConfiguration'] = new UntypedFormGroup({
      useIncentiveService: new UntypedFormControl(configuration.incentiveConfiguration.useIncentiveService),
      enhancedIncentives: new UntypedFormControl(configuration.incentiveConfiguration.enhancedIncentives),
      useVinSpecificIncentives: new UntypedFormControl(configuration.incentiveConfiguration.useVinSpecificIncentives)
    });
    businessCategoryFormItemsResult['leaseConfiguration'] = new UntypedFormGroup({
      showLeaseOffer: new UntypedFormControl(configuration.leaseConfiguration.showLeaseOffer),
      showAlternateLeaseOffer: new UntypedFormControl(configuration.leaseConfiguration.showAlternateLeaseOffer),
      leaseSellingPriceType: new UntypedFormControl(configuration.leaseConfiguration.leaseSellingPriceType),
      includeLeaseDealerIncentives: new UntypedFormControl(configuration.leaseConfiguration.includeLeaseDealerIncentives),
      leaseIncentiveType: new UntypedFormControl(configuration.leaseConfiguration.leaseIncentiveType),
      moneyFactorMarkup: new UntypedFormControl(configuration.leaseConfiguration.moneyFactorMarkup, [Validators.pattern(this.decimalPattern)]),
      calculateLeaseFrom: new UntypedFormControl(configuration.leaseConfiguration.calculateLeaseFrom),
      leaseDownDueAmount: new UntypedFormControl(configuration.leaseConfiguration.leaseDownDueAmount),
      leasePaymentType: new UntypedFormControl(configuration.leaseConfiguration.leasePaymentType),
      leaseTermSelection: new UntypedFormControl(leaseTermSelection),
      leasePricingBase: new UntypedFormControl(
        configuration.leaseConfiguration.leasePricingBase ?? this.leasePricingBaseDefault
      ),
      hideLeaseOfferWithDealerAddon: new UntypedFormControl(
        configuration.leaseConfiguration.hideLeaseOfferWithDealerAddon ?? true
      ),
      leasePaymentTerm: new UntypedFormControl(configuration.leaseConfiguration.leasePaymentTerm),
      leaseDownText: new UntypedFormControl(configuration.leaseConfiguration.leaseDownText),
      leaseMilesPerYear: new UntypedFormControl(configuration.leaseConfiguration.leaseMilesPerYear),
      leaseCentsPerMile: new UntypedFormControl(configuration.leaseConfiguration.leaseCentsPerMile),
      alternateLeaseCentsPerMile: new UntypedFormControl(configuration.leaseConfiguration.alternateLeaseCentsPerMile)
    });
    businessCategoryFormItemsResult['purchaseConfiguration'] = new UntypedFormGroup({
      dealerAddOnLabel: this.buildDealerAddOnLabelControl(configuration.purchaseConfiguration.dealerAddOnLabel),
      showPurchaseOffer: new UntypedFormControl(configuration.purchaseConfiguration.showPurchaseOffer),
      showMathBox: new UntypedFormControl(configuration.purchaseConfiguration.showMathBox),
      showSavingsOffer: new UntypedFormControl(configuration.purchaseConfiguration.showSavingsOffer),
      showPreRebateSubtotal: new UntypedFormControl(configuration.purchaseConfiguration.showPreRebateSubtotal),
      priceSubtotalLabel: new UntypedFormControl(
        configuration.purchaseConfiguration.priceSubtotalLabel ?? this.defaultPriceSubtotalLabel,
        [Validators.required, Validators.maxLength(this.priceSubtotalLabelMaxLength)]
      ),
      priceSubtotalFontSize: new UntypedFormControl(
        configuration.purchaseConfiguration.priceSubtotalFontSize ?? this.defaultPriceSubtotalLabelFontSize,
        [Validators.min(this.minimumPriceSubtotalFontSize)]
      ),
      priceSubtotalValueFontSize: new UntypedFormControl(
        configuration.purchaseConfiguration.priceSubtotalValueFontSize ?? this.defaultPriceSubtotalValueFontSize,
        [Validators.min(this.minimumPriceSubtotalFontSize)]
      ),
      priceSubtotalBold: new UntypedFormControl(configuration.purchaseConfiguration.priceSubtotalBold),
      showLineItemSuperscripts: new UntypedFormControl(configuration.purchaseConfiguration.showLineItemSuperscripts),
      includePurchaseDealerIncentives: new UntypedFormControl(configuration.purchaseConfiguration.includePurchaseDealerIncentives)
    });

    this.newMoneyFactorMarkupControl = businessCategoryFormItemsResult['leaseConfiguration'].controls.moneyFactorMarkup;
    this.calculateLeaseFromControl = businessCategoryFormItemsResult['leaseConfiguration'].controls.calculateLeaseFrom;
    this.leasePaymentTypeControl = businessCategoryFormItemsResult['leaseConfiguration'].controls.leasePaymentType;
    this.leaseTermSelectionControl = businessCategoryFormItemsResult['leaseConfiguration'].controls.leaseTermSelection;

    return businessCategoryFormItemsResult;
  }

  /**
   * Handle selection change
   */
  handleLeaseTermSelectionChange(): void
  {
    if (this.shouldShowLeaseTermSelectionExplanation()) {
      this.messageService.add({
        severity: this.leaseTermSelectionMessageSeverity,
        summary: this.leaseTermSelectionMessageSummary,
        detail: this.leaseTermSelectionExplanation,
        life: this.leaseTermSelectionMessageLife
      });
    }
  }

  /**
   * Initialize missing selection
   * @private
   */
  private initializeMissingLeaseTermSelection(): void
  {
    let leaseTermSelection: unknown = undefined;

    if (this.leaseTermSelectionControl !== null) {
      leaseTermSelection = this.leaseTermSelectionControl.value;
    }
    if (this.lowestEstimatedPaymentTermEnabled
      && this.leaseTermSelectionControl !== null
      && (leaseTermSelection === undefined || leaseTermSelection === null)) {
      this.leaseTermSelectionControl.setValue(this.fixedTermLeaseTermSelection, {emitEvent: false});
    }
  }

  /**
   * Boolean if explanation should be shown
   */
  shouldShowLeaseTermSelectionExplanation(): boolean
  {
    let shouldShowExplanation = false;

    if (this.lowestEstimatedPaymentTermEnabled && this.leaseTermSelectionControl !== null) {
      shouldShowExplanation = this.leaseTermSelectionControl.value === this.lowestEstimatedPaymentLeaseTermSelection;
    }

    return shouldShowExplanation;
  }

  /**
   * Helper function to build Used tab
   * @param businessCategory
   */
  handleBuildUsedForm(businessCategory) {
    const configuration = businessCategory['configuration'];
    const businessCategoryFormItemsResult = {}

    businessCategoryFormItemsResult['aprConfiguration'] = new UntypedFormGroup({
      showAprOffer: new UntypedFormControl(configuration.aprConfiguration.showAprOffer),
      aprPricingRate: new UntypedFormControl(configuration.aprConfiguration.aprPricingRate),
      aprPricingTerm: new UntypedFormControl(configuration.aprConfiguration.aprPricingTerm)
    });
    businessCategoryFormItemsResult['financeConfiguration'] = new UntypedFormGroup({
      showFinanceOffer: new UntypedFormControl(configuration.financeConfiguration.showFinanceOffer),
      financeAprSource: new UntypedFormControl(configuration.financeConfiguration.financeAprSource),
      hideFinanceOfferWithDealerAddon: new UntypedFormControl(
        configuration.financeConfiguration.hideFinanceOfferWithDealerAddon ?? false
      ),
      showFinancePayment: new UntypedFormControl(configuration.financeConfiguration.showFinancePayment),
      financePaymentApr: new UntypedFormControl(configuration.financeConfiguration.financePaymentApr),
      showFinanceApr: new UntypedFormControl(configuration.financeConfiguration.showFinanceApr),
      financePaymentTerm: new UntypedFormControl(configuration.financeConfiguration.financePaymentTerm),
      financeDownPaymentAmount: new UntypedFormControl(this.getFinanceDownPaymentAmountFormValue(
        configuration.financeConfiguration.financeDownPaymentAmount,
        configuration.financeConfiguration.financeDownPaymentType
      )),
      financeDownPaymentType: new UntypedFormControl(configuration.financeConfiguration.financeDownPaymentType || 'percent'),
      showFinanceDownPayment: new UntypedFormControl(configuration.financeConfiguration.showFinanceDownPayment),
      daysToFirstPayment: new UntypedFormControl(configuration.financeConfiguration.daysToFirstPayment)
    });
    businessCategoryFormItemsResult['generalConfiguration'] = new UntypedFormGroup({
      useRateTable: new UntypedFormControl(configuration.generalConfiguration.useRateTable)
    });
    businessCategoryFormItemsResult['leaseConfiguration'] = new UntypedFormGroup({
      showLeaseOffer: new UntypedFormControl(configuration.leaseConfiguration.showLeaseOffer),
      showAlternateLeaseOffer: new UntypedFormControl(configuration.leaseConfiguration.showAlternateLeaseOffer),
      leaseSellingPriceType: new UntypedFormControl(configuration.leaseConfiguration.leaseSellingPriceType),
      leaseIncentiveType: new UntypedFormControl(configuration.leaseConfiguration.leaseIncentiveType),
      moneyFactorMarkup: new UntypedFormControl(configuration.leaseConfiguration.moneyFactorMarkup, [Validators.pattern(this.decimalPattern)]),
      hideLeaseOfferWithDealerAddon: new UntypedFormControl(
        configuration.leaseConfiguration.hideLeaseOfferWithDealerAddon ?? true
      ),
      leasePaymentTerm: new UntypedFormControl(configuration.leaseConfiguration.leasePaymentTerm),
      leaseDownText: new UntypedFormControl(configuration.leaseConfiguration.leaseDownText),
      leaseMilesPerYear: new UntypedFormControl(configuration.leaseConfiguration.leaseMilesPerYear),
      leaseCentsPerMile: new UntypedFormControl(configuration.leaseConfiguration.leaseCentsPerMile),
      alternateLeaseCentsPerMile: new UntypedFormControl(configuration.leaseConfiguration.alternateLeaseCentsPerMile)
    });
    businessCategoryFormItemsResult['purchaseConfiguration'] = new UntypedFormGroup({
      showPurchaseOffer: new UntypedFormControl(configuration.purchaseConfiguration.showPurchaseOffer),
      showSavingsOffer: new UntypedFormControl(configuration.purchaseConfiguration.showSavingsOffer)
    });

    this.usedMoneyFactorMarkupControl = businessCategoryFormItemsResult['leaseConfiguration'].controls.moneyFactorMarkup;

    return businessCategoryFormItemsResult;
  }

  /**
   * Keep percentage values numeric so the persisted decimal matches a percentage option.
   */
  private getFinanceDownPaymentAmountFormValue(amount: unknown, type: unknown): unknown {
    if ((type === 'percent' || type === null || type === undefined || type === '')
      && amount !== null && amount !== undefined && amount !== '') {
      return Number(amount);
    }

    return amount;
  }

  /**
   * A value entered for one down-payment type must not be reinterpreted as the other type.
   */
  handleFinanceDownPaymentTypeChange(condition: 'New' | 'Used'): void {
    this.formItems[condition].financeConfiguration.controls.financeDownPaymentAmount.reset(null);
  }

  getFinancePaymentDownTypeOptions(condition: 'New' | 'Used') {
    return this.financePaymentDownTypeOptions;
  }

  isFinanceDownPaymentFromRateTable(condition: 'New' | 'Used'): boolean {
    return this.isUsingRateTable(condition)
      && this.formItems?.[condition]?.financeConfiguration.controls.financeDownPaymentType.value === 'dollar';
  }

  isUsingRateTable(condition: 'New' | 'Used'): boolean {
    const value = this.formItems?.[condition]?.generalConfiguration.controls.useRateTable.value;
    return value === true || value === 1 || value === '1';
  }

  /**
   * Helper function to build fees form
   * @param businessCategory
   */
  handleBuildFeesForm(businessCategory) {
    const feesConfiguration = businessCategory['configuration']['feesConfiguration'] ?? {};
    const accountBusinessCategoryGuid = businessCategory?.guid ?? null;
    const sharedFinalPriceText = this.getSharedDynamicFinalPriceText(feesConfiguration);
    const documentFee = this.getLegacyFeeFormValue(
      feesConfiguration['documentFee'],
      this.dynamicDocumentFeeType,
      accountBusinessCategoryGuid
    );
    const licenseFee = this.getLegacyFeeFormValue(
      feesConfiguration['licenseFee'],
      'licenseFee',
      accountBusinessCategoryGuid
    );
    const titleFee = this.getLegacyFeeFormValue(
      feesConfiguration['titleFee'],
      'titleFee',
      accountBusinessCategoryGuid
    );
    const acquisitionFee = this.getLegacyFeeFormValue(
      feesConfiguration['acquisitionFee'],
      'acquisitionFee',
      accountBusinessCategoryGuid
    );
    const taxes = this.getLegacyFeeFormValue(
      feesConfiguration['taxes'],
      this.dynamicTaxesType,
      accountBusinessCategoryGuid
    );
    const dispositionFee = this.getLegacyFeeFormValue(
      feesConfiguration['dispositionFee'],
      'dispositionFee',
      accountBusinessCategoryGuid
    );
    const customFee = this.getLegacyFeeFormValue(
      feesConfiguration['customFee'],
      this.dynamicCustomFeeType,
      accountBusinessCategoryGuid
    );
    const dynamicFeeRows = this.buildDynamicFeeRowsFormArray(feesConfiguration);

    return new UntypedFormGroup({
      documentFee: this.buildLegacyFeeFormGroup(documentFee),
      licenseFee: this.buildLegacyFeeFormGroup(licenseFee),
      titleFee: this.buildLegacyFeeFormGroup(titleFee),
      acquisitionFee: this.buildLegacyFeeFormGroup(acquisitionFee),
      taxes: this.buildLegacyFeeFormGroup(taxes),
      dispositionFee: this.buildLegacyFeeFormGroup(dispositionFee),
      customFee: this.buildLegacyCustomFeeFormGroup(customFee),
      rows: dynamicFeeRows,
      finalPriceText: new UntypedFormControl(sharedFinalPriceText)
    });
  }

  private getSharedDynamicFinalPriceText(feesConfiguration): string
  {
    let sharedFinalPriceText = this.dynamicDefaultFinalPriceText;
    const dynamicFeeRows = this.getDynamicFeeRows(feesConfiguration);

    dynamicFeeRows.forEach((feeRow) => {
      if (
        sharedFinalPriceText === this.dynamicDefaultFinalPriceText
        && typeof feeRow?.finalPriceText === 'string'
        && feeRow.finalPriceText !== ''
      ) {
        sharedFinalPriceText = feeRow.finalPriceText;
      }
    });

    if (
      sharedFinalPriceText === this.dynamicDefaultFinalPriceText
      && typeof feesConfiguration?.customFee?.finalPriceText === 'string'
      && feesConfiguration.customFee.finalPriceText !== ''
    ) {
      sharedFinalPriceText = feesConfiguration.customFee.finalPriceText;
    }

    return sharedFinalPriceText;
  }

  private getLegacyFeeFormValue(
    feeValue: LegacyFeeFormValue | null | undefined,
    feeType: string,
    accountBusinessCategoryGuid: string | null
  ): LegacyFeeFormValue
  {
    const normalizedFeeValue: LegacyFeeFormValue = {
      id: null,
      type: feeType,
      value: null,
      isPercent: false,
      leaseApplication: 'notUsed',
      financeApplication: 'notUsed',
      displayName: null,
      enabled: false,
      accountBusinessCategoryGuid: accountBusinessCategoryGuid,
      pricingOption: 'addToSalePrice',
      finalPriceText: this.dynamicDefaultFinalPriceText
    };

    if (feeValue !== null && feeValue !== undefined) {
      normalizedFeeValue.id = feeValue.id ?? null;
      normalizedFeeValue.value = feeValue.value ?? null;
      normalizedFeeValue.displayName = feeValue.displayName ?? null;
      normalizedFeeValue.accountBusinessCategoryGuid = feeValue.accountBusinessCategoryGuid ?? accountBusinessCategoryGuid;
      normalizedFeeValue.pricingOption = feeValue.pricingOption ?? 'addToSalePrice';
      normalizedFeeValue.finalPriceText = feeValue.finalPriceText ?? this.dynamicDefaultFinalPriceText;

      if (typeof feeValue.type === 'string' && feeValue.type !== '') {
        normalizedFeeValue.type = feeValue.type;
      }

      if (typeof feeValue.isPercent === 'boolean') {
        normalizedFeeValue.isPercent = feeValue.isPercent;
      }

      if (typeof feeValue.leaseApplication === 'string' && feeValue.leaseApplication !== '') {
        normalizedFeeValue.leaseApplication = feeValue.leaseApplication;
      }

      if (typeof feeValue.financeApplication === 'string' && feeValue.financeApplication !== '') {
        normalizedFeeValue.financeApplication = feeValue.financeApplication;
      }

      if (typeof feeValue.enabled === 'boolean') {
        normalizedFeeValue.enabled = feeValue.enabled;
      }
    }

    return normalizedFeeValue;
  }

  getLegacyFeeTypeLabel(categoryType: string, feeType: string): string
  {
    let feeTypeLabel = '';
    const typeValue = this.getLegacyFeeControlValue(categoryType, feeType, 'type');

    if (typeof typeValue === 'string') {
      feeTypeLabel = typeValue;
    }

    return feeTypeLabel;
  }

  getLegacyFeeValueSuffix(categoryType: string, feeType: string): string
  {
    let feeValueSuffix = '($)';
    const isPercent = this.getLegacyFeeControlValue(categoryType, feeType, 'isPercent');

    if (isPercent === true) {
      feeValueSuffix = '(%)';
    }

    return feeValueSuffix;
  }

  private getLegacyFeeControlValue(categoryType: string, feeType: string, controlName: string): unknown
  {
    let controlValue: unknown = null;
    const feeControl = this.masterFormGroup?.get([categoryType, 'feesConfiguration', feeType, controlName]);

    if (feeControl !== null && feeControl !== undefined) {
      controlValue = feeControl.value;
    }

    return controlValue;
  }

  private buildLegacyFeeFormGroup(feeValue: LegacyFeeFormValue): UntypedFormGroup
  {
    return new UntypedFormGroup({
      id: new UntypedFormControl(feeValue.id ?? null),
      type: new UntypedFormControl(feeValue.type ?? null),
      value: new UntypedFormControl(feeValue.value ?? null),
      isPercent: new UntypedFormControl(feeValue.isPercent ?? false),
      leaseApplication: new UntypedFormControl(feeValue.leaseApplication ?? 'notUsed'),
      financeApplication: new UntypedFormControl(feeValue.financeApplication ?? 'notUsed'),
      displayName: new UntypedFormControl(feeValue.displayName ?? null),
      enabled: new UntypedFormControl(feeValue.enabled ?? false),
      accountBusinessCategoryGuid: new UntypedFormControl(feeValue.accountBusinessCategoryGuid ?? null),
      pricingOption: new UntypedFormControl(feeValue.pricingOption ?? null),
      finalPriceText: new UntypedFormControl(feeValue.finalPriceText ?? null)
    });
  }

  private buildLegacyCustomFeeFormGroup(feeValue: LegacyFeeFormValue): UntypedFormGroup
  {
    return new UntypedFormGroup({
      id: new UntypedFormControl(feeValue.id ?? null),
      type: new UntypedFormControl(feeValue.type ?? this.dynamicCustomFeeType),
      value: new UntypedFormControl(feeValue.value ?? null),
      displayName: new UntypedFormControl(feeValue.displayName ?? null),
      enabled: new UntypedFormControl(feeValue.enabled ?? false),
      isPercent: new UntypedFormControl(false),
      leaseApplication: new UntypedFormControl('notUsed'),
      financeApplication: new UntypedFormControl('notUsed'),
      accountBusinessCategoryGuid: new UntypedFormControl(feeValue.accountBusinessCategoryGuid ?? null),
      pricingOption: new UntypedFormControl(feeValue.pricingOption ?? null),
      finalPriceText: new UntypedFormControl(feeValue.finalPriceText ?? null)
    });
  }

  private buildDynamicFeeRowsFormArray(feesConfiguration): UntypedFormArray
  {
    const feeRowControls: UntypedFormGroup[] = [];
    const dynamicFeeRows = this.getDynamicFeeRows(feesConfiguration);

    dynamicFeeRows.forEach((feeRow, index) => {
      feeRowControls.push(this.buildDynamicFeeRowFormGroup(feeRow, index));
    });

    return new UntypedFormArray(feeRowControls);
  }

  private getDynamicFeeRows(feesConfiguration): DynamicFeeRowFormValue[]
  {
    const dynamicFeeRows: DynamicFeeRowFormValue[] = [];

    if (feesConfiguration !== null && feesConfiguration !== undefined && Array.isArray(feesConfiguration.rows)) {
      feesConfiguration.rows.forEach((feeRow: DynamicFeeRowFormValue) => {
        if (this.shouldIncludeDynamicFeeRow(feeRow)) {
          dynamicFeeRows.push(feeRow);
        }
      });
    }

    return dynamicFeeRows;
  }

  private shouldIncludeDynamicFeeRow(feeRow: DynamicFeeRowFormValue): boolean
  {
    const feeType = this.resolveDynamicFeeRowType(feeRow);
    let shouldInclude = feeType === this.dynamicDocumentFeeType;

    if (this.isPositiveFeeValue(feeRow?.value)) {
      shouldInclude = true;
    }

    return shouldInclude;
  }

  private buildDynamicFeeRowFormGroup(feeRow: DynamicFeeRowFormValue, index: number): UntypedFormGroup
  {
    const feeType = this.resolveDynamicFeeRowType(feeRow);
    let isPercent = false;
    let isDefaultDocumentFee = false;
    let purchaseBehavior = this.dynamicPurchaseBehaviorNotUsed;
    let leaseBehavior = this.dynamicApplicationBehaviorIncludeInPayment;
    let financeBehavior = this.dynamicApplicationBehaviorIncludeInPayment;
    let enabled = true;
    const customFeeName = this.resolveDynamicCustomFeeName(feeRow);

    if (feeRow?.isPercent === true || feeType === this.dynamicTaxesType) {
      isPercent = true;
    }

    if (feeRow?.isDefaultDocumentFee === true) {
      isDefaultDocumentFee = true;
    }

    if (typeof feeRow?.purchaseBehavior === 'string' && feeRow.purchaseBehavior !== '') {
      purchaseBehavior = feeRow.purchaseBehavior;
    }

    if (typeof feeRow?.leaseBehavior === 'string' && feeRow.leaseBehavior !== '') {
      leaseBehavior = feeRow.leaseBehavior;
    }

    if (typeof feeRow?.financeBehavior === 'string' && feeRow.financeBehavior !== '') {
      financeBehavior = feeRow.financeBehavior;
    }

    if (typeof feeRow?.enabled === 'boolean') {
      enabled = feeRow.enabled;
    }

    return new UntypedFormGroup({
      id: new UntypedFormControl(feeRow?.id ?? null),
      type: new UntypedFormControl(feeType),
      customFeeName: new UntypedFormControl(customFeeName),
      value: new UntypedFormControl(feeRow?.value ?? null),
      isPercent: new UntypedFormControl(isPercent),
      purchaseBehavior: new UntypedFormControl(purchaseBehavior),
      purchaseDisplayName: new UntypedFormControl(feeRow?.purchaseDisplayName ?? null),
      leaseBehavior: new UntypedFormControl(leaseBehavior),
      financeBehavior: new UntypedFormControl(financeBehavior),
      isDefaultDocumentFee: new UntypedFormControl(isDefaultDocumentFee),
      sortOrder: new UntypedFormControl(feeRow?.sortOrder ?? index),
      enabled: new UntypedFormControl(enabled),
      accountBusinessCategoryGuid: new UntypedFormControl(feeRow?.accountBusinessCategoryGuid ?? null)
    });
  }

  private resolveDynamicFeeRowType(feeRow: DynamicFeeRowFormValue): string
  {
    let resolvedFeeType = this.getDefaultDynamicFeeType();

    if (typeof feeRow?.feeType === 'string' && feeRow.feeType !== '') {
      resolvedFeeType = feeRow.feeType;
    }

    if (typeof feeRow?.type === 'string' && feeRow.type !== '') {
      resolvedFeeType = feeRow.type;
    }

    return resolvedFeeType;
  }

  /**
   * Get Fee Type
   * @private
   */
  private getDefaultDynamicFeeType(): string
  {
    return this.dynamicDefaultFeeType;
  }

  /**
   * Resolve Custom Name
   * @param feeRow
   * @private
   */
  private resolveDynamicCustomFeeName(feeRow: DynamicFeeRowFormValue): string | null
  {
    let customFeeName = null;

    if (typeof feeRow?.customFeeName === 'string' && feeRow.customFeeName !== '') {
      customFeeName = feeRow.customFeeName;
    }

    if (customFeeName === null && typeof feeRow?.purchaseDisplayName === 'string' && feeRow.purchaseDisplayName !== '') {
      customFeeName = feeRow.purchaseDisplayName;
    }

    if (customFeeName === null && typeof feeRow?.displayName === 'string' && feeRow.displayName !== '') {
      customFeeName = feeRow.displayName;
    }

    return customFeeName;
  }

  /**
   * Build out FormControls and FormGroups for ad type settings form
   *
   * @param combinedFormItems
   */
  buildFormGroups(combinedFormItems) {
    const masterFormGroup = {};
    Object.keys(combinedFormItems).forEach((businessCategoryKey) => {
      masterFormGroup[businessCategoryKey] = {};
      const businessCategoryFormItems = combinedFormItems[businessCategoryKey];
      Object.keys(businessCategoryFormItems).forEach((settingsGroupKey) => {
          masterFormGroup[businessCategoryKey][settingsGroupKey] = businessCategoryFormItems[settingsGroupKey];
      });
      masterFormGroup[businessCategoryKey] = new UntypedFormGroup(masterFormGroup[businessCategoryKey]);
    });

    return new UntypedFormGroup(masterFormGroup);
  }

  /**
   * handler function called when Vehicle Ads switch is toggled
   */
  handleVehicleAdsEnabledToggle() {
    this.updateAdTypesAccountSettings(this.accountAdTypes.vehicleAdType);
    this.initializeBusinessCategoryConfigurations();
  }

  /**
   * Update ad types account settings (for now just toggle Vehicle Ads)
   *
   * @param adTypesAccountSettings
   */
  updateAdTypesAccountSettings(adTypesAccountSettings) {
    // Check if need to initialize or update ad types
    if (adTypesAccountSettings.guid === null) {
      adTypesAccountSettings.guid = '';
      this.adTypesSettingsService.createAccountAdTypes(adTypesAccountSettings)
        .subscribe(() => {
            this.getAdTypesAccountSettings().then(() => {
            });
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to initialize Ad Type settings',
              life: 5000
            });
          });
    } else {
      this.adTypesSettingsService.updateAdTypesAccountSettings(adTypesAccountSettings)
        .subscribe(
          () => {
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to save Ad type setting.',
              life: 5000
            });
          });
    }
  }

  /**
   * Initialize & populate business category configurations when Vehicle Ads is turned on for the first time
   */
  initializeBusinessCategoryConfigurations() {
    if ((this.accountAdTypes.vehicleAdType.enabled)
      && this.businessCategoryConfigurations === null) {
      this.businessCategoryConfigurationsService.initializeBusinessCategoryConfigurations(this.componentName, this.accountId)
        .subscribe(
          () => {
            this.getBusinessCategoryConfigurations(this.accountId);
          },
          () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to initialize configurations.',
              life: 5000
            });
          });
    }
  }

  /**
   * Vehicle Ads save form
   */
  handleVehicleAdsSave(): void
  {
    const shouldDisplayDocumentFeeWarning = this.shouldDisplayDocumentFeeWarning();

    if (shouldDisplayDocumentFeeWarning) {
      this.openDocumentFeeWarningModal();
    }

    if (!shouldDisplayDocumentFeeWarning) {
      this.continueVehicleAdsSave();
    }
  }

  /**
   * Saves Vehicle Ads settings without refreshing Offer Sets.
   */
  onVehicleAdsSaveWithoutSync(): void {
    this.showVehicleAdsSaveDialog = false;
    void this.saveVehicleAds(false);
  }

  /**
   * Saves Vehicle Ads settings and requests an Offer Set refresh.
   */
  onVehicleAdsSaveAndSync(): void {
    this.showVehicleAdsSaveDialog = false;
    void this.saveVehicleAds(true);
  }

  /**
   * Closes the Vehicle Ads save dialog without saving changes.
   */
  onVehicleAdsSaveCancelled(): void {
    this.showVehicleAdsSaveDialog = false;
  }

  /**
   * Continues the Vehicle Ads save flow based on the refresh feature state.
   */
  private continueVehicleAdsSave(): void {
    if (this.offerSetRecalculationOnSettingSaveEnabled) {
      this.showVehicleAdsSaveDialog = true;
    }

    if (!this.offerSetRecalculationOnSettingSaveEnabled) {
      void this.saveVehicleAds(false);
    }
  }

  private async saveVehicleAds(shouldSync: boolean): Promise<void>
  {
    if (!this.isSavingVehicleAds) {
      const affectedConditions = this.captureAffectedConditions();
      this.isSavingVehicleAds = true;
      try {
        await this.persistVehicleAdsChanges();
        this.handleVehicleAdsPersistenceSuccess();
        if (shouldSync) {
          await this.queueVehicleAdsFullRefresh(affectedConditions);
        }
      } catch (error: unknown) {
        this.handleVehicleAdsPersistenceError(error);
      } finally {
        this.isSavingVehicleAds = false;
      }
    }
  }

  /**
   * Persists all dirty Vehicle Ads configuration, offer rule, and incentive forms.
   */
  private async persistVehicleAdsChanges(): Promise<void> {
    const saveOperations: Promise<unknown>[] = [];

    if (this.masterFormGroup !== null && this.masterFormGroup.dirty) {
      const updatedConfigurations = this.mapConfigurationObject();
      const configurationSave = this.businessCategoryConfigurationsService
        .updateBusinessCategoryConfigurations(updatedConfigurations);
      saveOperations.push(firstValueFrom(configurationSave));
    }

    this.addOfferRuleSaveOperations(saveOperations);
    this.addConditionalIncentiveSaveOperations(saveOperations);
    await Promise.all(saveOperations);
  }

  /**
   * Adds dirty offer rule save operations to the pending save collection.
   */
  private addOfferRuleSaveOperations(saveOperations: Promise<unknown>[]): void {
    this.offerRulesComponents.forEach((offerRulesComponent) => {
      if (offerRulesComponent.offerRulesForm.dirty) {
        saveOperations.push(offerRulesComponent.saveRules());
      }
    });
  }

  /**
   * Adds dirty conditional incentive save operations to the pending save collection.
   */
  private addConditionalIncentiveSaveOperations(saveOperations: Promise<unknown>[]): void {
    this.conditionalIncentiveComponents.forEach((conditionalIncentiveComponent) => {
      if (conditionalIncentiveComponent.incentiveForm.dirty) {
        saveOperations.push(conditionalIncentiveComponent.saveIncentives());
      }
    });
  }

  /**
   * Captures the vehicle conditions affected by the pending settings changes.
   */
  private captureAffectedConditions(): OfferSetRefreshCondition[] {
    const conditions = new Set<OfferSetRefreshCondition>();
    this.addConfigurationConditions(conditions);
    this.addOfferRuleConditions(conditions);

    if (this.hasDirtyConditionalIncentiveForm()) {
      conditions.add(this.newRefreshCondition);
      conditions.add(this.usedRefreshCondition);
    }

    if (conditions.size === 0) {
      conditions.add(this.newRefreshCondition);
      conditions.add(this.usedRefreshCondition);
    }

    return Array.from(conditions);
  }

  /**
   * Adds conditions affected by dirty Vehicle Ads configuration forms.
   */
  private addConfigurationConditions(conditions: Set<OfferSetRefreshCondition>): void {
    if (this.masterFormGroup?.get('New')?.dirty) {
      conditions.add(this.newRefreshCondition);
    }
    if (this.masterFormGroup?.get('Used')?.dirty) {
      conditions.add(this.usedRefreshCondition);
    }
  }

  /**
   * Adds conditions affected by dirty offer rule forms.
   */
  private addOfferRuleConditions(conditions: Set<OfferSetRefreshCondition>): void {
    this.offerRulesComponents.forEach((offerRulesComponent) => {
      const condition = offerRulesComponent.condition.toLowerCase();
      if (offerRulesComponent.offerRulesForm.dirty && condition === this.newRefreshCondition) {
        conditions.add(this.newRefreshCondition);
      }
      if (offerRulesComponent.offerRulesForm.dirty && condition === this.usedRefreshCondition) {
        conditions.add(this.usedRefreshCondition);
      }
    });
  }

  /**
   * Determines whether any conditional incentive form has unsaved changes.
   */
  private hasDirtyConditionalIncentiveForm(): boolean {
    let isDirty = false;
    this.conditionalIncentiveComponents?.forEach((component) => {
      if (component.incentiveForm.dirty) {
        isDirty = true;
      }
    });

    return isDirty;
  }

  /**
   * Resets saved form state and displays the persistence success notification.
   */
  private handleVehicleAdsPersistenceSuccess(): void {
    this.masterFormGroup?.markAsPristine();
    this.dirtyOfferRulesForms = 0;
    this.dirtyConditionalIncentiveForms = 0;
    this.messageService.add({
      severity: 'success',
      summary: 'Update complete',
      detail: 'Your changes were saved.',
      life: this.standardNotificationLife
    });
  }

  /**
   * Logs a persistence failure and displays the save error notification.
   */
  private handleVehicleAdsPersistenceError(error: unknown): void {
    console.error('Unable to save all Vehicle Ads settings.', error);
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Unable to save all changes. No Offer Set refresh was queued.',
      life: this.standardNotificationLife
    });
  }

  /**
   * Queues a full Offer Set refresh for the affected vehicle conditions.
   */
  private async queueVehicleAdsFullRefresh(conditions: OfferSetRefreshCondition[]): Promise<void> {
    try {
      const request = this.adTypesSettingsService.queueOfferSetFullRefresh(this.accountId, conditions);
      const response = await firstValueFrom(request);
      this.handleFullRefreshQueued(response);
    } catch (error: unknown) {
      this.handleFullRefreshQueueError(error);
    }
  }

  /**
   * Displays the notification matching the accepted refresh dispatch state.
   */
  private handleFullRefreshQueued(response: OfferSetFullRefreshResponse): void {
    const fallbackQueued = response.dispatches.some((dispatch) => {

      return dispatch.dispatchState === this.fallbackDispatchState;
    });
    const alreadyActive = response.dispatches.some((dispatch) => {

      return dispatch.dispatchState === this.activeDispatchState;
    });
    let severity = 'info';
    let detail = `Settings saved. Offer Set refresh will complete in 5 minutes.`;

    if (fallbackQueued) {
      severity = 'warn';
      detail += ' One or more commands are queued for scheduler fallback.';
    }
    if (alreadyActive) {
      detail = 'Settings saved. Matching refresh work is already active.';
    }

    this.messageService.add({
      severity,
      detail,
      life: this.refreshNotificationLife
    });
  }

  /**
   * Logs a refresh queue failure and displays a retry notification.
   */
  private handleFullRefreshQueueError(error: unknown): void {
    console.error('Unable to queue the Offer Set full refresh.', error);
    this.messageService.add({
      severity: 'warn',
      summary: 'Settings saved',
      detail: 'Settings were saved, but the Offer Set refresh could not be queued. Please retry Save & Sync.',
      life: this.refreshNotificationLife
    });
  }

  /**
   * @private
   */
  private shouldDisplayDocumentFeeWarning(): boolean
  {
    let shouldDisplayDocumentFeeWarning = false;

    if (this.dynamicFeesTableEnabled && this.masterFormGroup !== null) {
      shouldDisplayDocumentFeeWarning = this.hasMisconfiguredDocumentFee();
    }

    return shouldDisplayDocumentFeeWarning;
  }

  /**
   * @private
   */
  private hasMisconfiguredDocumentFee(): boolean
  {
    let hasMisconfiguredDocumentFee = false;
    const businessCategoryKeys = Object.keys(this.masterFormGroup.controls);

    businessCategoryKeys.forEach((businessCategoryKey) => {
      const feesConfiguration = this.masterFormGroup.get([businessCategoryKey, 'feesConfiguration']) as UntypedFormGroup | null;

      if (this.isDocumentFeeMisconfigured(feesConfiguration)) {
        hasMisconfiguredDocumentFee = true;
      }
    });

    return hasMisconfiguredDocumentFee;
  }

  /**
   * @param feesConfiguration
   * @private
   */
  private isDocumentFeeMisconfigured(feesConfiguration: UntypedFormGroup | null): boolean
  {
    let isDocumentFeeMisconfigured = false;
    const documentFeeRows = this.getDocumentFeeRows(feesConfiguration);

    if (documentFeeRows.length === 0) {
      isDocumentFeeMisconfigured = true;
    }

    if (documentFeeRows.length > 0 && !this.hasConfiguredDocumentFeeValue(documentFeeRows)) {
      isDocumentFeeMisconfigured = true;
    }

    return isDocumentFeeMisconfigured;
  }

  /**
   * @param feesConfiguration
   * @private
   */
  private getDocumentFeeRows(feesConfiguration: UntypedFormGroup | null): UntypedFormGroup[]
  {
    const documentFeeRows: UntypedFormGroup[] = [];
    const feeRowsControl = feesConfiguration?.get('rows');

    if (feeRowsControl instanceof UntypedFormArray) {
      feeRowsControl.controls.forEach((feeRowControl) => {
        const feeRow = feeRowControl as UntypedFormGroup;
        const feeType = feeRow.get('type')?.value;

        if (feeType === this.dynamicDocumentFeeType) {
          documentFeeRows.push(feeRow);
        }
      });
    }

    return documentFeeRows;
  }

  /**
   * @param documentFeeRows
   * @private
   */
  private hasConfiguredDocumentFeeValue(documentFeeRows: UntypedFormGroup[]): boolean
  {
    let hasConfiguredDocumentFeeValue = false;

    documentFeeRows.forEach((documentFeeRow) => {
      const feeValue = documentFeeRow.get('value')?.value;

      if (this.isPositiveFeeValue(feeValue)) {
        hasConfiguredDocumentFeeValue = true;
      }
    });

    return hasConfiguredDocumentFeeValue;
  }

  /**
   * @param feeValue
   * @private
   */
  private isPositiveFeeValue(feeValue: unknown): boolean
  {
    let isPositiveFeeValue = false;
    const parsedFeeValue = parseFloat(String(feeValue));

    if (!Number.isNaN(parsedFeeValue) && parsedFeeValue > 0) {
      isPositiveFeeValue = true;
    }

    return isPositiveFeeValue;
  }

  /**
   * @private
   */
  private openDocumentFeeWarningModal(): void
  {
    this.confirmationService.confirm({
      key: this.documentFeeWarningModalKey,
      defaultFocus: 'none',
      message: this.documentFeeWarningMessage,
      header: this.documentFeeWarningHeader,
      rejectButtonProps: {
        label: 'Return to Fee Configuration',
        severity: 'secondary'
      },
      acceptButtonProps: {
        label: 'Save Without Document Fee',
        severity: 'warn'
      },
      accept: () => {
        this.confirmationService.close();
        this.continueVehicleAdsSave();
      },
      reject: () => {
        this.confirmationService.close();
      }
    });
  }

  /**
   * Helper function to map the form values to the object used in the request body
   */
  mapConfigurationObject(): object {
    const updatedConfiguration = this.businessCategoryConfigurations;
    const rawFormValues = this.masterFormGroup.getRawValue() as Record<string, BusinessCategoryConfigurationFormValue>;
    const formValuesMap = new Map<string, BusinessCategoryConfigurationFormValue>(Object.entries(rawFormValues));

    formValuesMap.forEach((configurationValue, key) => {
      const businessCategoryType = updatedConfiguration.businessCategoryTypes.find(item => item.name === key);

      if (businessCategoryType !== undefined) {
        const nestedConfiguration = configurationValue;

        this.normalizeCalculationSettings(nestedConfiguration);
        this.normalizeDealerAddOnLabelPayload(nestedConfiguration, key);
        nestedConfiguration.feesConfiguration = this.convertFeesToNumber(nestedConfiguration.feesConfiguration);
        businessCategoryType.configuration = nestedConfiguration;
      }
    });

    return updatedConfiguration;
  }

  /**
   * Converts numeric form-control values to the nullable strings expected by the
   * business category configuration API.
   */
  private normalizeCalculationSettings(configuration: BusinessCategoryConfigurationFormValue): void {
    this.normalizeFieldsAsNullableStrings(configuration['aprConfiguration'], [
      'aprPricingRate',
      'aprPricingTerm'
    ]);
    this.normalizeFieldsAsNullableStrings(configuration['financeConfiguration'], [
      'financePaymentApr',
      'financePaymentTerm',
      'financeDownPaymentAmount',
      'daysToFirstPayment'
    ]);
    this.normalizeFieldsAsNullableStrings(configuration['leaseConfiguration'], [
      'leasePaymentTerm',
      'leaseMilesPerYear',
      'leaseCentsPerMile',
      'alternateLeaseCentsPerMile'
    ]);
  }

  private normalizeFieldsAsNullableStrings(configuration: unknown, fieldNames: string[]): void {
    if (typeof configuration !== 'object' || configuration === null) {
      return;
    }

    const configurationValues = configuration as Record<string, unknown>;

    fieldNames.forEach((fieldName) => {
      const value = configurationValues[fieldName];

      if (value === null || value === undefined || value === '') {
        configurationValues[fieldName] = null;
      } else {
        configurationValues[fieldName] = String(value);
      }
    });
  }

  /**
   * Helper function to convert fee values from strings to numbers since the Prime Faces
   * form component converts numeric input fields to strings when calling getValue()
   *
   * @param feesConfiguration
   */
  convertFeesToNumber(feesConfiguration: FeesConfigurationFormValue): FeesConfigurationFormValue {
    const convertedConfigurations = feesConfiguration;

    convertedConfigurations.documentFee.value = +convertedConfigurations.documentFee.value;
    convertedConfigurations.licenseFee.value = +convertedConfigurations.licenseFee.value;
    convertedConfigurations.titleFee.value = +convertedConfigurations.titleFee.value;
    convertedConfigurations.acquisitionFee.value = +convertedConfigurations.acquisitionFee.value;
    convertedConfigurations.taxes.value = +convertedConfigurations.taxes.value;
    convertedConfigurations.dispositionFee.value = +convertedConfigurations.dispositionFee.value;
    convertedConfigurations.customFee.value = +convertedConfigurations.customFee.value;

    if (!this.dynamicFeesTableEnabled) {
      this.syncLegacyFeesToDynamicRows(convertedConfigurations);
    }

    if (Array.isArray(convertedConfigurations.rows)) {
      convertedConfigurations.rows.forEach((feeRow) => {
        feeRow.feeType = this.resolveDynamicFeeRowType(feeRow);

        if (this.dynamicFeesTableEnabled) {
          this.normalizeDynamicFeeTitle(feeRow);
        }

        if (feeRow.value === '') {
          feeRow.value = null;
        }

        if (feeRow.value !== null) {
          feeRow.value = +feeRow.value;
        }
      });
    }

    this.applySharedFinalPriceTextToRows(convertedConfigurations);

    return convertedConfigurations;
  }

  private normalizeDynamicFeeTitle(feeRow: DynamicFeeRowFormValue): void
  {
    let normalizedTitle = this.getDynamicFeeTypeLabel(feeRow.feeType);

    if (typeof feeRow.purchaseDisplayName === 'string' && feeRow.purchaseDisplayName.trim() !== '') {
      normalizedTitle = feeRow.purchaseDisplayName.trim();
    }

    feeRow.purchaseDisplayName = normalizedTitle;
  }

  private getDynamicFeeTypeLabel(feeType: string | null | undefined): string | null
  {
    let feeTypeLabel = null;

    if (typeof feeType === 'string' && Object.hasOwn(this.dynamicFeeTypeLabels, feeType)) {
      feeTypeLabel = this.dynamicFeeTypeLabels[feeType];
    }

    return feeTypeLabel;
  }

  private syncLegacyFeesToDynamicRows(feesConfiguration: FeesConfigurationFormValue): void
  {
    const synchronizedRows = this.copyDynamicFeeRows(feesConfiguration.rows);

    this.getLegacyFeeTypes().forEach((feeType, index) => {
      this.syncLegacyFeeToDynamicRows(feesConfiguration, synchronizedRows, feeType, index);
    });

    feesConfiguration.rows = synchronizedRows;
  }

  private copyDynamicFeeRows(feeRows: DynamicFeeRowFormValue[] | undefined): DynamicFeeRowFormValue[]
  {
    const copiedRows: DynamicFeeRowFormValue[] = [];

    if (Array.isArray(feeRows)) {
      feeRows.forEach((feeRow) => {
        copiedRows.push({...feeRow});
      });
    }

    return copiedRows;
  }

  private syncLegacyFeeToDynamicRows(
    feesConfiguration: FeesConfigurationFormValue,
    synchronizedRows: DynamicFeeRowFormValue[],
    feeType: string,
    sortOrder: number
  ): void
  {
    const legacyFee = feesConfiguration[feeType] as LegacyFeeFormValue;
    const existingRowIndex = synchronizedRows.findIndex((feeRow) => this.resolveDynamicFeeRowType(feeRow) === feeType);
    const shouldSynchronize = this.shouldSynchronizeLegacyFee(legacyFee, feeType);

    if (shouldSynchronize) {
      const synchronizedRow = this.buildLegacySynchronizedRow(
        legacyFee,
        feeType,
        synchronizedRows,
        sortOrder,
        feesConfiguration.finalPriceText
      );
      this.upsertLegacySynchronizedRow(synchronizedRows, synchronizedRow, existingRowIndex);
    }

    if (!shouldSynchronize && existingRowIndex >= 0) {
      synchronizedRows.splice(existingRowIndex, 1);
    }
  }

  private shouldSynchronizeLegacyFee(legacyFee: LegacyFeeFormValue, feeType: string): boolean
  {
    let shouldSynchronize = feeType === this.dynamicDocumentFeeType;

    if (this.isPositiveFeeValue(legacyFee?.value)) {
      shouldSynchronize = true;
    }

    return shouldSynchronize;
  }

  private upsertLegacySynchronizedRow(
    synchronizedRows: DynamicFeeRowFormValue[],
    synchronizedRow: DynamicFeeRowFormValue,
    existingRowIndex: number
  ): void
  {
    if (existingRowIndex >= 0) {
      synchronizedRows[existingRowIndex] = synchronizedRow;
    }

    if (existingRowIndex < 0) {
      synchronizedRows.push(synchronizedRow);
    }
  }

  private getLegacyFeeTypes(): string[]
  {
    return [
      this.dynamicDocumentFeeType,
      this.dynamicLicenseFeeType,
      this.dynamicTitleFeeType,
      this.dynamicAcquisitionFeeType,
      this.dynamicTaxesType,
      this.dynamicDispositionFeeType,
      this.dynamicCustomFeeType
    ];
  }

  private buildLegacySynchronizedRow(
    legacyFee: LegacyFeeFormValue,
    feeType: string,
    existingRows: DynamicFeeRowFormValue[],
    sortOrder: number,
    sharedFinalPriceText: string | null | undefined
  ): DynamicFeeRowFormValue
  {
    const synchronizedRow = this.getExistingLegacyFeeRow(existingRows, feeType);
    const normalizedPurchaseBehavior = this.resolveLegacyRowPurchaseBehavior(legacyFee, feeType);
    const normalizedFinalPriceText = this.resolveLegacyRowFinalPriceText(legacyFee, synchronizedRow, sharedFinalPriceText);

    synchronizedRow.type = feeType;
    synchronizedRow.feeType = feeType;
    synchronizedRow.id = legacyFee?.id ?? synchronizedRow.id ?? null;
    synchronizedRow.value = legacyFee?.value ?? null;
    synchronizedRow.isPercent = legacyFee?.isPercent ?? synchronizedRow.isPercent ?? false;
    synchronizedRow.purchaseBehavior = normalizedPurchaseBehavior;
    synchronizedRow.purchaseDisplayName = legacyFee?.displayName ?? synchronizedRow.purchaseDisplayName ?? null;
    synchronizedRow.leaseBehavior = legacyFee?.leaseApplication ?? synchronizedRow.leaseBehavior ?? this.dynamicApplicationBehaviorNotUsed;
    synchronizedRow.financeBehavior = legacyFee?.financeApplication ?? synchronizedRow.financeBehavior ?? this.dynamicApplicationBehaviorNotUsed;
    synchronizedRow.isDefaultDocumentFee = synchronizedRow.isDefaultDocumentFee ?? false;
    synchronizedRow.sortOrder = synchronizedRow.sortOrder ?? sortOrder;
    synchronizedRow.enabled = legacyFee?.enabled ?? synchronizedRow.enabled ?? false;
    synchronizedRow.accountBusinessCategoryGuid = legacyFee?.accountBusinessCategoryGuid ?? synchronizedRow.accountBusinessCategoryGuid ?? null;
    synchronizedRow.finalPriceText = legacyFee?.finalPriceText ?? synchronizedRow.finalPriceText ?? normalizedFinalPriceText;

    if (feeType === this.dynamicCustomFeeType) {
      synchronizedRow.customFeeName = legacyFee?.displayName ?? synchronizedRow.customFeeName ?? null;
    }

    if (feeType !== this.dynamicCustomFeeType) {
      synchronizedRow.customFeeName = null;
    }

    return synchronizedRow;
  }

  private resolveLegacyRowPurchaseBehavior(
    legacyFee: LegacyFeeFormValue,
    feeType: string
  ): string
  {
    let purchaseBehavior = this.dynamicApplicationBehaviorNotUsed;

    if (
      feeType === this.dynamicCustomFeeType
      && typeof legacyFee?.pricingOption === 'string'
      && legacyFee.pricingOption !== ''
    ) {
      purchaseBehavior = legacyFee.pricingOption;
    }

    return purchaseBehavior;
  }

  private resolveLegacyRowFinalPriceText(
    legacyFee: LegacyFeeFormValue,
    synchronizedRow: DynamicFeeRowFormValue,
    sharedFinalPriceText: string | null | undefined
  ): string
  {
    let finalPriceText = synchronizedRow.finalPriceText ?? this.dynamicDefaultFinalPriceText;

    if (typeof sharedFinalPriceText === 'string' && sharedFinalPriceText !== '') {
      finalPriceText = sharedFinalPriceText;
    }

    if (typeof legacyFee?.finalPriceText === 'string' && legacyFee.finalPriceText !== '') {
      finalPriceText = legacyFee.finalPriceText;
    }

    return finalPriceText;
  }

  private getExistingLegacyFeeRow(existingRows: DynamicFeeRowFormValue[], feeType: string): DynamicFeeRowFormValue
  {
    let existingLegacyFeeRow: DynamicFeeRowFormValue = {value: null};

    existingRows.forEach((feeRow) => {
      if (this.resolveDynamicFeeRowType(feeRow) === feeType) {
        existingLegacyFeeRow = {...feeRow};
      }
    });

    return existingLegacyFeeRow;
  }

  private applySharedFinalPriceTextToRows(feesConfiguration: FeesConfigurationFormValue): void
  {
    let sharedFinalPriceText = this.dynamicDefaultFinalPriceText;

    if (typeof feesConfiguration.finalPriceText === 'string' && feesConfiguration.finalPriceText !== '') {
      sharedFinalPriceText = feesConfiguration.finalPriceText;
    }

    if (Array.isArray(feesConfiguration.rows)) {
      feesConfiguration.rows.forEach((feeRow) => {
        feeRow.finalPriceText = sharedFinalPriceText;
      });
    }
  }


  /**
   * Moves an item in a FormArray to another position
   * @param formArray
   * @param fromIndex
   * @param toIndex
   */
  moveItemInFormArray(
    formArray: UntypedFormArray,
    fromIndex: number,
    toIndex: number
  ): void {
    const direction = toIndex > fromIndex ? 1 : -1;

    const item = formArray.at(fromIndex);
    for (let iteration = fromIndex; iteration * direction < toIndex * direction; iteration = iteration + direction) {
      const current = formArray.at(iteration + direction);
      formArray.setControl(iteration, current);
    }
    formArray.setControl(toIndex, item);
  }

  /**
   * Method to handle the dirty state of the offer rules form
   * @param isDirty
   */
  handleOfferRulesFormDirty(isDirty: boolean) {
    if (isDirty) {
      this.dirtyOfferRulesForms += 1;
    } else {
      this.dirtyOfferRulesForms -= 1;
    }
  }

  /**
   * Gets whether the save button should be disabled or not
   */
  get isSaveDisabled(): boolean {
    let isSaveDisabled = true;

    if (this.masterFormGroup !== null) {
      isSaveDisabled = !this.hasUnsavedVehicleAdsChanges()
        || !this.masterFormGroup.valid
        || !this.conditionalIncentiveFormValid;
    }

    return isSaveDisabled;
  }

  /**
   * @private
   */
  private hasUnsavedVehicleAdsChanges(): boolean
  {
    const hasConfigurationChanges = this.masterFormGroup !== null && this.masterFormGroup.dirty;
    const hasUnsavedChanges = hasConfigurationChanges
      || this.hasDirtyOfferRuleForm()
      || this.hasDirtyConditionalIncentiveForm();

    return hasUnsavedChanges;
  }

  /**
   * Determines whether any offer rule form has unsaved changes.
   */
  private hasDirtyOfferRuleForm(): boolean {
    let isDirty = false;
    this.offerRulesComponents?.forEach((component) => {
      if (component.offerRulesForm.dirty) {
        isDirty = true;
      }
    });

    return isDirty;
  }

  /**
   * Method to handle the dirty state of the conditional incentive form
   * @param isDirty
   */
  handleConditionalIncentiveFormDirty(isDirty: boolean) {
    if (isDirty === true) {
      this.dirtyConditionalIncentiveForms += 1;
    } else if (isDirty === false) {
      this.dirtyConditionalIncentiveForms -= 1;
    }
  }

  /**
   * Method to handle the validity state of the conditional incentive form
   * @param isValid
   */
  setConditionalIncentiveFormValid(isValid: boolean) {
    this.conditionalIncentiveFormValid = isValid;
  }

  /**
   * Method to handle the beforeunload event to prevent users from navigating away from the page
   * @param event
   */
  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: Event): void {
    if (this.hasUnsavedVehicleAdsChanges()) {
      event.preventDefault();
    }
  }

  /**
   * Method to handle the delete of the offer rules
   */
  handleDeleteOfferRules() {
    this.messageService.add({
      severity: 'success',
      summary: 'Delete Successful',
      detail: 'Ad Rule(s) deleted successfully.',
      life: 5000
    });
  }

  /**
   * Method to handle toggling of enhanced incentives
   */
  handleEnhancedIncentivesToggle() {
    this.messageService.add({
      severity: 'warn',
      summary: 'Warning: Action Required',
      detail: 'Please delete and re-create all New ads after turning Enhanced Incentives on or off.',
      life: 10000
    });
  }

  /**
   * Sets the controls for the incentive toggles that are dependent on the use incentive service setting
   */
  setIncentiveControlsStatus() {
    if (this.formItems['New'].incentiveConfiguration.controls.useIncentiveService.value === false) {
      this.formItems['New'].incentiveConfiguration.controls.enhancedIncentives.disable();
      this.formItems['New'].incentiveConfiguration.controls.useVinSpecificIncentives.disable();
      this.formItems['New'].generalConfiguration.controls.includeSelectInventoryIncentives.disable();
      this.formItems['New'].incentiveConfiguration.controls.enhancedIncentives.setValue(false);
      this.formItems['New'].incentiveConfiguration.controls.useVinSpecificIncentives.setValue(false);
      this.formItems['New'].generalConfiguration.controls.includeSelectInventoryIncentives.setValue(false);
    } else {
      this.formItems['New'].incentiveConfiguration.controls.enhancedIncentives.enable();
      this.formItems['New'].incentiveConfiguration.controls.useVinSpecificIncentives.enable();
      this.formItems['New'].generalConfiguration.controls.includeSelectInventoryIncentives.enable();
    }
  }

  /**
   * Gets the disabled tool tip if criteria is not met
   */
  getDisabledToolTip() {
    let result = null;

    if (this.masterFormGroup !== null && !this.masterFormGroup.valid) {
      result = 'Please complete all required fields before saving.';
    }

    if (this.masterFormGroup !== null && this.masterFormGroup.valid && !this.hasUnsavedVehicleAdsChanges()) {
      result = 'Make a change before saving.';
    }

    return result;
  }
}
